import React, { useState, useEffect } from "react"
import {
  PlusCircleIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import {
  GetSitemapEntries,
  DeleteSitemapEntries,
  GenerateSitemap,
  GetSitemapProviders,
} from "./api"
import EditDialog from "./dialogs/edit-dialog"
import RestoreDialog from "./dialogs/restore-dialog"

export default function SitemapBrowser() {
  const [selectedItems, setSelectedItems] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false)
  const [currentEditItem, setCurrentEditItem] = useState(null)
  const [originFilter, setOriginFilter] = useState([
    { value: null, label: "All" },
  ]) // Changed to array
  const [providers, setProviders] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)

  // Load providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await GetSitemapProviders()
        if (response.result && response.providers) {
          setProviders(response.providers)
        }
      } catch (error) {
        console.error("Failed to fetch providers:", error)
      }
    }

    fetchProviders()
  }, [])

  // Handle refreshing the sitemap
  const handleRefreshSitemap = async () => {
    setIsGenerating(true)

    try {
      const response = await GenerateSitemap()

      if (response.result) {
        showNotification({
          message: `Sitemap generated successfully with ${
            response.entriesCount || 0
          } entries`,
          className: "bg-admin_primary text-admin_text_inv",
        })

        // Refresh the table
        setRefreshKey((prev) => prev + 1)
      } else {
        showNotification({
          message: response.message || "Failed to generate sitemap",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while generating the sitemap",
        className: "bg-admin_error text-white",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  // Handle adding a new item
  const handleAddItem = () => {
    setCurrentEditItem(null)
    setIsEditDialogOpen(true)
  }

  // Handle editing an item
  const handleEditItem = (item) => {
    setCurrentEditItem(item)
    setIsEditDialogOpen(true)
  }

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedItems([])
  }

  // Handle deleting selected items
  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) return

    showPromptDialog({
      title: "Confirm Delete",
      message: `Are you sure you want to delete ${
        selectedItems.length
      } sitemap ${selectedItems.length === 1 ? "entry" : "entries"}?`,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          const response = await DeleteSitemapEntries(selectedItems)

          if (response.result) {
            showNotification({
              message: `${selectedItems.length} ${
                selectedItems.length === 1 ? "entry" : "entries"
              } deleted successfully`,
              className: "bg-admin_primary text-admin_text_inv",
            })

            // Reset selection and refresh
            setSelectedItems([])
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.message || "Failed to delete entries",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle saving an item (add or edit)
  const handleSaveItem = () => {
    setRefreshKey((prev) => prev + 1)
  }

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection change
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Format providers for dropdown
  const getProviderOptions = () => {
    const items = providers.map((provider) => ({
      value: provider,
      label: provider.charAt(0).toUpperCase() + provider.slice(1),
    }))

    //add the All option
    items.unshift({
      value: null,
      label: "All",
    })
    return items
  }

  // Handle the onChange event from DropDownSelect for the origin filter
  const handleOriginFilterChange = (selectedValues) => {
    // Save the entire array for multiSelection mode
    setOriginFilter(selectedValues)
    setRefreshKey((prev) => prev + 1)
  }

  // Build request fields based on filters
  const getRequestFields = () => {
    const match = {}

    // Check if we have selected items other than "All"
    const selectedOrigins = originFilter
      .filter((item) => item.value !== null)
      .map((item) => item.value)

    if (selectedOrigins.length > 0) {
      // If we have specific origins selected, create a query for them
      match["content.origin"] = { $in: selectedOrigins }
    }

    return { match }
  }

  // Style overrides for the DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    header: "w-full flex items-center gap-3 my-4",
    searchInput: "flex-1 max-w-sm",
    headerControls: "shrink-0",
    getRowStyle: (index, item) => {
      // Check if this item is selected
      if (item && selectedItems.includes(item._id)) {
        return "bg-admin_canvas" // Selected row style
      }
    },
  }

  // Table columns
  const columns = [
    {
      id: "select",
      headerClassName: "w-10",
      header: (value, items) => (
        <Checkbox
          variant="admin"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedItems.includes(item._id)}
          onChange={(isSelected) => handleSelectItem(item._id, isSelected)}
        />
      ),
    },
    {
      id: "url",
      title: "URL",
      headerClassName: "w-1/2",
      render: (value, item) => (
        <div className="truncate max-w-md">{item.url}</div>
      ),
    },
    {
      id: "frequency",
      title: "Frequency",
      headerClassName: "w-1/8  text-center",
      render: (value, item) => (
        <div className="text-center">{item.frequency || "-"}</div>
      ),
    },
    {
      id: "priority",
      title: "Priority",
      headerClassName: "w-1/8 text-center",
      render: (value, item) =>(
        <div className="text-center">
          {item.priority !== undefined ? item.priority.toFixed(1) : "-"}
        </div>
      )
        
    },
    {
      id: "origin",
      title: "Origin",
      headerClassName: "w-1/8 text-center",
      render: (value, item) => (
        <div className="text-center">
          {item.origin || "manual"}
        </div>
      )
    },
    {
      id: "edit",
      title: "Edit",
      headerClassName: "w-1/8 text-center",
      render: (value, item) => (
        <div className="flex justify-center">
          <Button 
            variant="admin_icon" 
            className="text-admin_text_secondary"
            onClick={() => handleEditItem(item)}
          >
            <PencilIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ]
  // Header controls
  const headerControls = (
    <div className="flex items-center gap-3">
      {/* Origin filter */}
      {providers.length > 0 && (
        <DropDownSelect
          variant="admin"
          options={getProviderOptions()}
          value={originFilter}
          onChange={handleOriginFilterChange}
          placeholder="Select origin"
          multiSelection
          className="min-w-[180px]"
        />
      )}
    </div>
  )

  const tableFooter = selectedItems.length > 0 && (
     <div className="flex items-center justify-end gap-4 bg-admin_paper rounded   p-3 mt-4">
      <Typography variant="admin_body">
        {selectedItems.length}{" "}
        {selectedItems.length === 1 ? "item" : "items"} selected
      </Typography>

      <Button
        variant="admin_secondary"
        onClick={handleClearSelection}
      >
        <div className="flex items-center gap-1">
          <XMarkIcon className="w-5 h-5" />
          Deselect
        </div>
      </Button>

      <Button
        variant="admin_error"
        onClick={handleDeleteSelected}
      >
        <div className="flex items-center gap-1">
          <TrashIcon className="w-5 h-5" />
          Delete
        </div>
      </Button>
    </div>
  )

  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Sitemap Entries
        </Typography>

        <div className="flex gap-2 items-center">
          {/* Refresh sitemap button */}
          <Button
            variant="admin_secondary"
            onClick={handleRefreshSitemap}
            disabled={isGenerating}
            title="Generate sitemap.xml file using all entries and deploy to configured destinations"
          >
            <div className="flex items-center space-x-2">
              <ArrowPathIcon
                className={`w-5 h-5 mr-2 ${isGenerating ? "animate-spin" : ""}`}
              />
              {isGenerating ? "Generating..." : "Generate"}
            </div>
          </Button>

          <Button
            variant="admin_secondary"
            onClick={() => setIsRestoreDialogOpen(true)}
          >
            Restore
          </Button>

          <Button 
            variant="admin_primary" 
            onClick={handleAddItem}
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add
          </Button>
        </div>
      </div>

      {/* Main data table */}
      <DataTable
        variant="admin_browser"
        apiMethod={GetSitemapEntries}
        preferredHeight="65vh"
        headerControls={headerControls}
        tableFooter={tableFooter}
        itemHeight="h-[70px]"
        searchField="content.url content.origin"
        requestFields={getRequestFields()}
        sort={{ createdAt: -1 }}
        localUserPreferencesKey="admin.sitemap.pages"
        renders={columns}
        refreshKey={refreshKey}
        styleOverrides={styleOverrides} // Use styleOverrides instead of rowClassName
        noData={
          <div className="p-4 text-center">
            <Typography variant="admin_h3" className="mb-2">
              No sitemap entries found
            </Typography>
            <Typography
              variant="admin_body_dim"
            >
              {originFilter.length > 0 && originFilter[0].value !== null
                ? `No entries found with the selected origins`
                : "Try adding entries manually or register data providers"}
            </Typography>
          </div>
        }
      />

      {/* Dialogs */}
      <EditDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        initialData={currentEditItem}
        onSave={handleSaveItem}
        parentRefreshKey={refreshKey}
      />

      <RestoreDialog
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        parentRefreshKey={refreshKey}
        onItemsRestored={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  )
}
